import React, { useEffect, useRef } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import homeData from "../../data/home.json";
import { Users } from "lucide-react";
import api from "../../api/api";
import { useQuery } from "@tanstack/react-query";

/* ─────────────────────────────────────────────
 LOGO CARD
───────────────────────────────────────────── */
const LogoCard = ({ item }) => {
  const ref = useRef(null);
  const imgRef = useRef(null);

  // Normalize mapping to handle both static JSON structure and API structure
  const logoSrc = item.logoUrl;

  const displayName = item.name;

  const handleEnter = () => {
    gsap.to(ref.current, {
      y: -4,
      scale: 1.05,
      // borderColor: "rgba(29,78,216,0.35)",
      // backgroundColor: "rgba(29,78,216,0.06)",
      duration: 0.3,
      ease: "power2.out",
    });
    gsap.to(imgRef.current, {
      filter: "brightness(1) saturate(1)",
      duration: 0.3,
    });
  };

  const handleLeave = () => {
    gsap.to(ref.current, {
      y: 0,
      scale: 1,
      // borderColor: "rgba(203,213,225,0.3)",
      // backgroundColor: "rgba(248,250,252,0.8)",
      duration: 0.3,
      ease: "power2.out",
    });
    gsap.to(imgRef.current, {
      // filter: "brightness(0.8) saturate(0.7)",
      duration: 0.3,
    });
  };

  return (
    <div
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="flex-shrink-0 flex-col items-center justify-center
 rounded-2xl px-8 py-5 cursor-default mx-5   "
      title={displayName}
    >
      {logoSrc ? (
        <img
          ref={imgRef}
          src={`${process.env.REACT_APP_BACKEND_URL}${logoSrc}`}
          alt={displayName}
          className="max-h-20 max-w-[120px] object-contain mb-3"
          onError={(e) => {
            /* fallback to text if logo fails */
            e.currentTarget.style.display = "none";
            e.currentTarget.nextSibling.style.display = "flex";
          }}
        />
      ) : null}
      {/* Text fallback */}
      <div className="w-full flex flex-row">
        <span
          className="w-full  text-center  text-xs font-bold text-gray-600
      tracking-widest uppercase text-center"
        >
          {displayName}
        </span>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 INFINITE MARQUEE ROW
───────────────────────────────────────────── */
const MarqueeRow = ({ items, direction = "left", speed = 40, label }) => {
  const trackRef = useRef(null);
  const wrapRef = useRef(null);

  /* GSAP infinite scroll */
  useEffect(() => {
    const track = trackRef.current;
    if (!track || items.length === 0) return;

    const totalW = track.scrollWidth / 2;
    const duration = totalW / speed;

    const anim = gsap.to(track, {
      x: direction === "left" ? -totalW : totalW,
      duration,
      ease: "none",
      repeat: -1,
      modifiers: {
        x: gsap.utils.unitize((x) => {
          const pos = parseFloat(x);
          if (direction === "left") {
            return pos <= -totalW ? 0 : pos;
          } else {
            return pos >= 0 ? -totalW : pos;
          }
        }),
      },
    });

    /* Pause on hover */
    const handleEnter = () => anim.pause();
    const handleLeave = () => anim.play();
    wrapRef.current?.addEventListener("mouseenter", handleEnter);
    wrapRef.current?.addEventListener("mouseleave", handleLeave);

    return () => {
      anim.kill();
      wrapRef.current?.removeEventListener("mouseenter", handleEnter);
      wrapRef.current?.removeEventListener("mouseleave", handleLeave);
    };
  }, [direction, speed, items]); // Added items dependency so GSAP updates when data loads

  if (!items || items.length === 0) return null;

  /* Duplicate items for seamless loop */
  const doubled = [...items, ...items];

  return (
    <div className="space-y-3">
      {/* Row label */}
      <div className="flex items-center gap-3 px-2">
        <div className="h-px flex-1 bg-gray-200" />
        <div className="flex items-center gap-2">
          <Users size={11} className="text-primary-400" />
          <span className="text-[10px] font-semibold tracking-[0.25em] uppercase text-gray-600">
            {label}
          </span>
        </div>
        <div className="h-px flex-1 bg-gray-200" />
      </div>

      {/* Marquee */}
      <div ref={wrapRef} className="overflow-hidden relative">
        {/* Left fade */}
        <div
          className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to right, #ffffff, transparent)",
          }}
        />
        {/* Right fade */}
        <div
          className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none"
          style={{
            background: "linear-gradient(to left, #ffffff, transparent)",
          }}
        />

        <div
          ref={trackRef}
          className="flex items-center"
          style={{
            width: "max-content",
            transform:
              direction === "right" ? `translateX(-${50}%)` : "translateX(0)",
          }}
        >
          {doubled.map((item, i) => (
            <LogoCard key={`${item.id || i}-${i}`} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 SECTION HEADER
───────────────────────────────────────────── */
const SectionHeader = ({ data }) => {
  const labelRef = useRef(null);
  const headRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 85%",
        },
      });
      tl.fromTo(
        labelRef.current,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" },
      )
        .fromTo(
          headRef.current?.children ?? [],
          { y: 40, opacity: 0, skewY: 2 },
          {
            y: 0,
            opacity: 1,
            skewY: 0,
            stagger: 0.12,
            duration: 0.7,
            ease: "power4.out",
          },
          "-=0.2",
        )
        .fromTo(
          lineRef.current,
          { scaleX: 0 },
          {
            scaleX: 1,
            duration: 0.6,
            ease: "power3.out",
            transformOrigin: "center",
          },
          "-=0.3",
        );
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="text-center space-y-4 mb-14">
      <div ref={headRef} className="overflow-hidden space-y-1">
        <h2
          className="font-display font-black text-gray-900 leading-tight"
          style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
        >
          {data?.headline || "Our Partners & Clients"}
        </h2>
        <h2
          className="font-display font-black text-gradient-primary leading-tight"
          style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
        >
          {data?.headlineAccent || "Driving Global Impact"}
        </h2>
      </div>
      <div
        ref={lineRef}
        className="h-[2px] w-20 mx-auto rounded-full"
        style={{
          background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
          transform: "scaleX(0)",
          transformOrigin: "center",
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN CLIENTS & PARTNERS
───────────────────────────────────────────── */
const ClientsPartners = () => {
  const { clients: localData } = homeData;
  const sectionRef = useRef(null);
  const rowsRef = useRef(null);

  // Fetch from live database. Asking for limit=100 so all logos show up in marquee.
  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ["partners"],
    queryFn: async () => {
      const response = await api.get("/api/partners?limit=100");
      return response.data; // Ensure this extracts the actual Axios response object body
    },
  });

  /* Rows reveal */
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        rowsRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: rowsRef.current,
            start: "top 85%",
          },
        },
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  // Compute live data vs static fallback fallback
  const hasLivePartners =
    apiResponse?.partners && apiResponse.partners.length > 0;
  const activeItems = hasLivePartners
    ? apiResponse.partners
    : [...localData.clients, ...localData.partners];

  // Dynamic counter metrics
  const clientCount = hasLivePartners
    ? apiResponse.partners.filter((item) => item.type == "client").length
    : localData.clients.length;
  const partnerCount = hasLivePartners
    ? apiResponse.partners.filter((item) => item.type == "partner").length
    : localData.partners.length;

  return (
    <section
      ref={sectionRef}
      id="clients"
      className="relative overflow-hidden section-py"
      style={{
        background:
          "linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)",
      }}
    >
      {/* BG */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-pattern opacity-[0.05]" />
        <div
          className="absolute inset-0"
          style={{
            background: `
 radial-gradient(ellipse 60% 40% at 50% 50%,
 rgba(29,78,216,0.08) 0%, transparent 60%)
`,
          }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-24"
          style={{
            background:
              "linear-gradient(to bottom, #ffffff 0%, transparent 100%)",
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-24"
          style={{
            background: "linear-gradient(to top, #ffffff 0%, transparent 100%)",
          }}
        />
      </div>

      <div className="container-fluid relative z-10">
        <SectionHeader data={localData} />
      </div>

      {/* Full-width marquee rows */}
      <div
        ref={rowsRef}
        className="space-y-8 relative z-10"
        style={{ opacity: 0 }}
      >
        <MarqueeRow
          items={activeItems}
          direction="left"
          speed={35}
          label="Partners & Clients"
        />
      </div>

      {/* Bottom count strip */}
      <div className="container-fluid relative z-10 mt-12">
        <div className="flex items-center justify-center gap-8 flex-wrap">
          {[
            { value: clientCount + "+", label: "Client Organizations" },
            { value: partnerCount + "+", label: "Strategic Partners" },
            { value: "15+", label: "Countries Reached" },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 text-center">
              {i > 0 && (
                <div className="w-px h-8 bg-gray-300 hidden sm:block" />
              )}
              <div>
                <p className="font-black font-mono text-2xl text-secondary-400">
                  {s.value}
                </p>
                <p className="text-xs text-gray-600">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientsPartners;
